import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Building2, FlaskConical, MapPin, Calendar, Globe, Activity,
  Sparkles, Share2, FileText, Mail, Check, X, MessageSquare,
  TrendingUp, Target, ChevronRight, Beaker, Pill, Eye, Lock,
} from 'lucide-react';

/**
 * DemoPage — a self-contained, read-only walkthrough of ForesightRx for
 * showcasing the platform to prospective biotech clients. All data on this
 * page is HARDCODED mock data (NovaCure Bio, a fictional Shanghai-based
 * oncology biotech). The page makes ZERO backend calls — there is no risk
 * of leaking real client data or corrupting production collections.
 *
 * Three tabs walk a prospect through the typical engagement in 5-7 minutes:
 *   1. Biotech Profile — how their company looks in our CRM
 *   2. AI Mapping     — Claude-generated ranked target list for one asset
 *   3. Shareable Link — the public status page they receive to track progress
 */

// ─────────────────────────────────────────────────────────────────────────────
// Mock biotech persona — Shanghai oncology biotech with three assets
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_COMPANY = {
  name: 'NovaCure Bio',
  legal_name: 'NovaCure Biosciences (Shanghai) Co., Ltd.',
  hq: 'Shanghai, China',
  founded: 2018,
  website: 'novacure.bio',
  team_size: '~85 FTE',
  funding_stage: 'Series C',
  funding_raised: '$142M USD',
  ta_focus: 'Oncology · Autoimmune',
  short_pitch:
    'Clinical-stage biotech advancing first-in-class small-molecule and bispecific antibody therapeutics in solid tumors and rheumatology, with two Phase 2 assets and a global-first IND in CNS-penetrant ROS1 inhibition.',
  leadership: [
    { name: 'Dr. Wei Chen', role: 'CEO & Co-founder', prior: 'BeiGene · Roche Shanghai' },
    { name: 'Lisa Park', role: 'Chief Business Officer', prior: 'Innovent · J&J Asia BD' },
    { name: 'Dr. Marcus Liang', role: 'CMO', prior: 'AstraZeneca Oncology' },
  ],
};

const MOCK_ASSETS = [
  {
    id: 'nvc-101',
    name: 'NVC-101',
    modality: 'Small Molecule',
    target: 'ROS1 / NTRK (CNS-penetrant)',
    indication: 'NSCLC (ROS1+), Solid Tumors',
    phase: 'Phase 2',
    moa: 'Brain-penetrant next-gen TKI overcoming G2032R resistance',
    differentiation:
      'Best-in-class CNS exposure (Kp,uu 0.8) vs. entrectinib (0.4) and lorlatinib (0.6). Demonstrated tumor regression in 7/9 lorlatinib-refractory patients in dose-escalation.',
    ip_status: 'Composition of matter to 2041 (US, EU, JP, CN granted)',
    next_milestone: 'P2 readout Q3 2026',
    geo: 'Ex-China rights available',
    is_lead: true,
  },
  {
    id: 'nvc-204',
    name: 'NVC-204',
    modality: 'Bispecific Antibody (TCE)',
    target: 'CLDN18.2 × CD3',
    indication: 'Gastric Cancer, Pancreatic Cancer',
    phase: 'Phase 1',
    moa: '2+1 format with engineered low-affinity CD3 arm — wider therapeutic index than first-gen TCEs',
    differentiation:
      'CRS Grade ≥2 incidence only 9% in monotherapy dose escalation (vs. 30%+ benchmark). Two confirmed PRs at RP2D.',
    ip_status: 'COM filings in US/EU/CN; expected 2042 expiry',
    next_milestone: 'P1 dose-expansion data H1 2026',
    geo: 'Global rights available',
    is_lead: false,
  },
  {
    id: 'nvc-318',
    name: 'NVC-318',
    modality: 'Oral Small Molecule',
    target: 'TYK2 (allosteric)',
    indication: 'Psoriasis, IBD',
    phase: 'Preclinical (IND-ready)',
    moa: 'Pseudokinase-domain inhibitor with >1000× selectivity vs JAK1/2/3',
    differentiation:
      'In NHP models, achieved deucravacitinib-comparable PASI suppression at 1/3 the dose with no LFT signal.',
    ip_status: 'COM filed 2025',
    next_milestone: 'IND submission Q2 2026',
    geo: 'Global rights available',
    is_lead: false,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Client-stated target preferences — these REPLACE the old "approve/reject"
// notion. Instead of letting the biotech rank pharma post-hoc (which would
// disclose our scoring), we ask upfront which buyers they want pursued and
// which they want excluded. The mapping engine then runs strictly within
// those guardrails.
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_INTEREST_LIST = [
  { company: 'Pfizer', reason: 'CEO knows their oncology BD lead from prior role at Array' },
  { company: 'AstraZeneca', reason: 'Strong CNS-penetrant TKI franchise (Tagrisso) — natural strategic fit' },
  { company: 'Takeda', reason: 'Existing Japan partner; warm intro through their Shanghai BD office' },
  { company: 'Bristol Myers Squibb', reason: 'Active interest in Chinese-originated assets via Shanghai BD' },
];

const MOCK_EXCLUSION_LIST = [
  { company: 'Roche', reason: 'Direct competitive overlap (Rozlytrek) — board prefers to avoid' },
  { company: 'Merck KGaA', reason: 'Active exclusivity discussions on a separate NVC-204 partnership' },
  { company: 'Bayer', reason: 'Failed prior CDA cycle (2024) — exclude to avoid relationship strain' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Simulator widgets — Non-Confidential Deck, Data Room, CDA tracker, BD
// activity timeline. These give a prospect a holistic feel for the platform
// in one screen instead of one feature at a time.
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_DECK = {
  title: 'NovaCure Bio — Non-Confidential Overview',
  version: 'v3.2',
  updated: 'Feb 14, 2026',
  pages: 12,
  status: 'Shared with 6 pharma',
  thumbs: [
    { n: 1, label: 'Cover · Platform Thesis' },
    { n: 4, label: 'NVC-101 · ROS1 / NTRK · P2 readout' },
    { n: 7, label: 'NVC-204 · CLDN18.2×CD3 TCE · P1 data' },
    { n: 10, label: 'IP & Patent Strategy' },
  ],
};

const MOCK_DATA_ROOM = {
  total_files: 14,
  total_size: '184 MB',
  categories: [
    { key: 'Pitch Deck', count: 2, color: 'sky' },
    { key: 'Preclinical', count: 5, color: 'violet' },
    { key: 'Clinical', count: 3, color: 'emerald' },
    { key: 'CMC', count: 2, color: 'amber' },
    { key: 'IP', count: 1, color: 'rose' },
    { key: 'Regulatory', count: 1, color: 'slate' },
  ],
  sample_files: [
    { name: 'NVC-101_P2_interim_topline.pdf', cat: 'Clinical', size: '3.4 MB' },
    { name: 'NVC-204_CRS_safety_summary.pdf', cat: 'Preclinical', size: '1.8 MB' },
    { name: 'Composition_of_matter_summary.docx', cat: 'IP', size: '420 KB' },
  ],
};

const MOCK_CDA = [
  { pharma: 'Pfizer',        status: 'Finalised',          signed: 'Feb 04, 2026', expires: 'Feb 04, 2028', tone: 'emerald' },
  { pharma: 'AstraZeneca',   status: 'Finalised',          signed: 'Feb 09, 2026', expires: 'Feb 09, 2028', tone: 'emerald' },
  { pharma: 'Takeda',        status: 'Counter-signed',     signed: 'Feb 11, 2026', expires: '—',           tone: 'blue' },
  { pharma: 'Roche',         status: 'Under legal review', signed: '—',           expires: '—',            tone: 'amber' },
  { pharma: 'BMS',           status: 'Drafted',            signed: '—',           expires: '—',            tone: 'slate' },
];

const MOCK_ACTIVITY = [
  { when: 'Feb 14',  icon: 'mail',     text: 'Personalised outreach sent — 7 Tier-1 pharma, asset-specific positioning' },
  { when: 'Feb 11',  icon: 'doc',      text: 'AstraZeneca CDA fully executed; Data Room access granted' },
  { when: 'Feb 09',  icon: 'meeting',  text: 'Takeda call — Asia BD lead + Oncology TA head; positive interest in NVC-101' },
  { when: 'Feb 07',  icon: 'doc',      text: 'Pfizer counter-signed CDA; deck shared in next 24h' },
  { when: 'Feb 04',  icon: 'mail',     text: 'CDA + non-confidential deck dispatched to Pfizer following intro call' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Mock AI mapping output for the lead asset (NVC-101)
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_TARGETS = [
  {
    company: 'Pfizer',
    country: 'USA',
    tier: 'Tier 1',
    fit: 'Strategic',
    score: 42,
    confidence: 'High',
    archetype: 'Big Pharma · Oncology Builder',
    deal_type: 'Ex-China License / M&A',
    rationale:
      "Pfizer's loss of Xalkori exclusivity (2027) and the Seagen integration have created an explicit gap in next-gen TKIs. NVC-101's CNS coverage directly addresses the unmet need that drove Pfizer to acquire Array (2019) and Trillium (2021). Recent BD signals show active screening of ROS1 assets.",
  },
  {
    company: 'AstraZeneca',
    country: 'UK',
    tier: 'Tier 1',
    fit: 'Strategic',
    score: 40,
    confidence: 'High',
    archetype: 'Big Pharma · Precision Oncology',
    deal_type: 'Ex-China License',
    rationale:
      "AZ's Tagrisso (EGFR) franchise sets the bar for CNS-penetrant TKIs and they have a stated thesis on brain mets. The Daiichi ADC alliance reduced internal capacity for small-molecule discovery — perfect window for in-licensing a clinical-stage asset with mature CMC.",
  },
  {
    company: 'Takeda',
    country: 'Japan',
    tier: 'Tier 1',
    fit: 'Strategic',
    score: 39,
    confidence: 'High',
    archetype: 'Big Pharma · Oncology',
    deal_type: 'Asia-ex-China License',
    rationale:
      "Takeda inherited Iclusig from Ariad and has actively replenished its TKI portfolio. Japan-led Asia commercialization is a clear strategic fit, and their China JV (Takeda China) provides natural geographic complementarity rather than overlap.",
  },
  {
    company: 'Roche',
    country: 'Switzerland',
    tier: 'Tier 2',
    fit: 'Adjacent',
    score: 34,
    confidence: 'High',
    archetype: 'Big Pharma · Oncology',
    deal_type: 'Co-development',
    rationale:
      "Roche has Rozlytrek (entrectinib) — direct competitive overlap creates two-sided dynamic: defensive interest is high but they may prefer combinations over displacement. Best positioned as a combo partner (NVC-101 + Tecentriq) rather than monotherapy bet.",
  },
  {
    company: 'Bristol Myers Squibb',
    country: 'USA',
    tier: 'Tier 2',
    fit: 'Adjacent',
    score: 32,
    confidence: 'Medium',
    archetype: 'Big Pharma · IO Builder',
    deal_type: 'Ex-China License',
    rationale:
      "BMS lacks a meaningful precision-oncology TKI franchise outside of Sprycel (BCR-ABL). Strategic but not urgent — interest hinges on positioning NVC-101 within their IO-combination thesis. BD team known to evaluate Chinese-originated assets via their Shanghai BD office.",
  },
  {
    company: 'Merck KGaA',
    country: 'Germany',
    tier: 'Tier 3',
    fit: 'Opportunistic',
    score: 27,
    confidence: 'Medium',
    archetype: 'Mid Pharma · Oncology',
    deal_type: 'Ex-China License',
    rationale:
      "Building oncology franchise post-Tepmetko launch; capacity exists but bandwidth constrained by recent in-licensing activity. Worth approaching but expect slower decision velocity.",
  },
  {
    company: 'Boehringer Ingelheim',
    country: 'Germany',
    tier: 'Tier 3',
    fit: 'Opportunistic',
    score: 25,
    confidence: 'Medium',
    archetype: 'Mid Pharma · Oncology Expansion',
    deal_type: 'Ex-China License',
    rationale:
      "Privately held with patient capital — historically open to in-licensing clinical-stage assets. KRAS focus may deprioritise ROS1, but solid-tumor pipeline gap remains.",
  },
  {
    company: 'Bayer',
    country: 'Germany',
    tier: 'Tier 4',
    fit: 'Opportunistic',
    score: 22,
    confidence: 'Low',
    archetype: 'Big Pharma · Oncology (Divesting)',
    deal_type: 'Ex-China License',
    rationale:
      "Has Vitrakvi (NTRK) — adjacent franchise. Recent oncology restructuring signals reduced appetite for new licensing. Include for completeness but expect limited engagement.",
  },
];

// Mock funnel/outreach state for the shareable status page preview.
// Status NAMES MIRROR the real CRM's vocabulary exactly (see
// `STATUS_COLORS` in `PublicStatusPage.js`) so a prospect sees the same
// terms here that their real biotech detail page will use.
const MOCK_RESPONSES = [
  // ─── Most advanced ────────────────────────────────────────────────────
  {
    company: 'Pfizer', country: 'USA',
    status: 'Due Diligence',
    sub_reason: 'Reviewing P2 interim data + CMC package; questions from clinical team queued',
    last_touch: 'Feb 14 · Data Room accessed (3 files)',
    next_event: 'Internal review committee · Feb 26',
  },
  {
    company: 'AstraZeneca', country: 'UK',
    status: 'CDA Signed',
    sub_reason: 'CDA executed; non-confidential deck delivered, awaiting feedback from oncology TA head',
    last_touch: 'Feb 11 · Acknowledgement received',
    next_event: 'Follow-up call requested · w/o Feb 23',
  },

  // ─── Active engagement / evaluating ──────────────────────────────────
  {
    company: 'Takeda', country: 'Japan',
    status: 'Meeting Scheduled',
    sub_reason: 'Asia BD lead + Oncology TA head joined intro call; positive interest in Asia-ex-China rights',
    last_touch: 'Feb 09 · Intro call held',
    next_event: 'Technical deep-dive · Feb 24',
  },
  {
    company: 'Roche', country: 'Switzerland',
    status: 'CDA Signed',
    sub_reason: 'Mutual NDA in counter-party legal review; standard 2-3 week turnaround',
    last_touch: 'Feb 07 · Draft exchanged',
    next_event: 'Expected execution · early Mar',
  },
  {
    company: 'Bristol Myers Squibb', country: 'USA',
    status: 'Awaiting Trial Data',
    sub_reason: 'Shanghai BD office interested; want to see Phase 2 readout before committing diligence resources',
    last_touch: 'Feb 02 · Initial response',
    next_event: 'Re-engage post-P2 topline · Q3 2026',
  },
  {
    company: 'Merck KGaA', country: 'Germany',
    status: 'Asset Under Evaluation',
    sub_reason: 'Non-confidential deck shared; routed to oncology BD committee for evaluation',
    last_touch: 'Jan 29 · Materials submitted',
    next_event: 'Committee response expected · w/o Mar 02',
  },

  // ─── Declined (with explicit reason) ─────────────────────────────────
  {
    company: 'Bayer', country: 'Germany',
    status: 'Declined',
    sub_reason: 'Strategic misfit — oncology restructuring in 2025 reduced appetite for new in-licensing this cycle',
    last_touch: 'Jan 24 · Decline letter received',
    next_event: 'Re-evaluate post-strategy-review · 2027',
  },
  {
    company: 'Sanofi', country: 'France',
    status: 'Declined',
    sub_reason: 'Therapeutic-area mismatch — recent pivot away from solid-tumor small-molecule franchise',
    last_touch: 'Feb 03 · Decline communicated',
    next_event: '—',
  },
  {
    company: 'Novartis', country: 'Switzerland',
    status: 'Declined',
    sub_reason: 'Overlapping internal program — disclosed only that competitive asset is in late discovery',
    last_touch: 'Feb 06 · Decline communicated',
    next_event: '—',
  },
];

// Real CRM status palette — mirrors `STATUS_COLORS` in PublicStatusPage.js
// so the demo uses the exact same colors a biotech client will see in
// production. (Keep these in sync if the production palette changes.)
const REAL_STATUS_COLORS = {
  'Introduction Made':      'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Email Acknowledged':     'bg-lime-50 text-lime-700 border-lime-200',
  'Asset Under Evaluation': 'bg-amber-50 text-amber-700 border-amber-200',
  'Meeting Scheduled':      'bg-violet-50 text-violet-700 border-violet-200',
  'CDA Signed':             'bg-blue-50 text-blue-700 border-blue-200',
  'Awaiting Trial Data':    'bg-orange-50 text-orange-700 border-orange-200',
  'Due Diligence':          'bg-indigo-50 text-indigo-700 border-indigo-200',
  'In Negotiation':         'bg-purple-50 text-purple-700 border-purple-200',
  'Deal Closed':            'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Declined':               'bg-rose-50 text-rose-600 border-rose-200',
};

const TIER_COLORS = {
  'Tier 1': 'bg-emerald-600 text-white',
  'Tier 2': 'bg-blue-500 text-white',
  'Tier 3': 'bg-amber-500 text-white',
  'Tier 4': 'bg-purple-500 text-white',
};
const FIT_COLORS = {
  Strategic: 'bg-emerald-100 text-emerald-700',
  Adjacent: 'bg-blue-100 text-blue-700',
  Opportunistic: 'bg-amber-100 text-amber-700',
};
const TONE_DOT = {
  emerald: 'bg-emerald-500',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  slate: 'bg-slate-400',
  rose: 'bg-rose-500',
};

// ─────────────────────────────────────────────────────────────────────────────
// Watermark — visible on every tab so a prospect never confuses this with prod
// ─────────────────────────────────────────────────────────────────────────────
function DemoBanner() {
  return (
    <div
      className="sticky top-0 z-30 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 text-white px-5 py-2 flex items-center justify-between"
      data-testid="demo-banner"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" />
        <span className="text-[11px] font-bold uppercase tracking-widest">Demo Mode</span>
        <span className="text-[11px] opacity-90 hidden sm:inline">
          — fictional biotech, simulated data. Nothing on this page touches your real CRM.
        </span>
      </div>
      <Eye className="w-3.5 h-3.5 opacity-80" />
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, num, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium rounded-md transition-all ${
        active
          ? 'bg-slate-900 text-white shadow-sm'
          : 'text-slate-500 hover:text-slate-900 hover:bg-white'
      }`}
    >
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
      }`}>{num}</span>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 1 — Biotech Profile (NovaCure Bio company page mockup)
// ─────────────────────────────────────────────────────────────────────────────
function ProfileTab() {
  return (
    <div className="space-y-5" data-testid="demo-profile-tab">
      {/* Hero */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-violet-500/20 text-violet-200 border border-violet-400/30 text-[10px]">DEMO</Badge>
                <Badge className="bg-white/10 text-white border border-white/20 text-[10px]">{MOCK_COMPANY.funding_stage}</Badge>
                <Badge className="bg-white/10 text-white border border-white/20 text-[10px]">{MOCK_COMPANY.ta_focus}</Badge>
              </div>
              <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {MOCK_COMPANY.name}
              </h1>
              <p className="text-xs text-slate-400 mt-1">{MOCK_COMPANY.legal_name}</p>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-300">
                <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{MOCK_COMPANY.hq}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />Founded {MOCK_COMPANY.founded}</span>
                <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" />{MOCK_COMPANY.website}</span>
                <span className="flex items-center gap-1.5"><Activity className="w-3 h-3" />{MOCK_COMPANY.team_size}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Total Raised</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{MOCK_COMPANY.funding_raised}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 bg-white">
          <p className="text-sm text-slate-700 leading-relaxed">{MOCK_COMPANY.short_pitch}</p>
        </div>
      </Card>

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Pipeline Assets', value: MOCK_ASSETS.length, icon: FlaskConical, tone: 'fuchsia' },
          { label: 'Clinical-stage', value: MOCK_ASSETS.filter(a => a.phase.startsWith('Phase')).length, icon: Beaker, tone: 'sky' },
          { label: 'Lead in Phase 2', value: 1, icon: Pill, tone: 'emerald' },
          { label: 'Geographic Scope', value: 'Global', icon: Globe, tone: 'amber' },
        ].map((s, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</p>
              <s.icon className={`w-3.5 h-3.5 text-${s.tone}-500`} />
            </div>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Leadership */}
      <Card className="p-5">
        <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Leadership</h2>
        <div className="grid grid-cols-3 gap-4">
          {MOCK_COMPANY.leadership.map((p, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`demo-leader-${i}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                {p.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                <p className="text-[11px] text-slate-600">{p.role}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Prior: {p.prior}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Pipeline */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Pipeline</h2>
          <span className="text-[10px] text-slate-400">{MOCK_ASSETS.length} assets · Click any to drill in (demo)</span>
        </div>
        <div className="space-y-3">
          {MOCK_ASSETS.map((a, i) => (
            <Card key={a.id} className="p-5 hover:border-slate-300 transition-colors" data-testid={`demo-asset-${i}`}>
              <div className="flex items-start gap-4">
                <div className={`w-1 self-stretch rounded-full ${a.is_lead ? 'bg-fuchsia-500' : 'bg-slate-200'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{a.name}</h3>
                    {a.is_lead && <Badge className="bg-fuchsia-100 text-fuchsia-700 text-[10px]">Lead Asset</Badge>}
                    <Badge variant="outline" className="text-[10px]">{a.modality}</Badge>
                    <Badge className="bg-slate-900 text-white text-[10px]">{a.phase}</Badge>
                    <span className="text-[10px] text-slate-400">{a.geo}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    <span className="font-semibold text-slate-700">{a.target}</span> · {a.indication}
                  </p>
                  <p className="text-xs text-slate-700 leading-relaxed mb-2">
                    <span className="font-semibold">MoA:</span> {a.moa}
                  </p>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    <span className="font-semibold text-slate-700">Differentiation:</span> {a.differentiation}
                  </p>
                  <div className="flex items-center gap-4 text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                    <span><span className="text-slate-400">IP:</span> {a.ip_status}</span>
                    <span><span className="text-slate-400">Next:</span> <span className="text-emerald-600 font-medium">{a.next_milestone}</span></span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Simulator widgets — give the prospect a holistic feel of the
            platform: non-confidential deck snapshot, Data Room, CDA tracker,
            and a live BD activity timeline. ───────────────────────────── */}

      {/* Row 1: Non-Confidential Deck + Data Room (side by side) */}
      <div className="grid grid-cols-2 gap-4" data-testid="demo-deck-dataroom">
        {/* Non-confidential deck snapshot */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-slate-900 to-slate-700 px-5 py-3 flex items-center justify-between">
            <div>
              <p className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Non-Confidential Deck</p>
              <p className="text-sm font-semibold text-white mt-0.5">{MOCK_DECK.title}</p>
            </div>
            <Badge className="bg-white/10 text-white border border-white/20 text-[10px]">{MOCK_DECK.version}</Badge>
          </div>
          <div className="px-5 py-3 grid grid-cols-2 gap-2">
            {MOCK_DECK.thumbs.map((t, i) => (
              <div key={i} className="aspect-[4/3] rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden group" data-testid={`demo-deck-thumb-${i}`}>
                <div className="absolute top-1 left-1.5 text-[8px] font-bold text-slate-300">{String(t.n).padStart(2,'0')}</div>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
                  <FileText className="w-4 h-4 text-slate-400 mb-1" />
                  <p className="text-[9px] text-slate-600 leading-tight">{t.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-2.5 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
            <span>{MOCK_DECK.pages} pages · Updated {MOCK_DECK.updated}</span>
            <span className="text-emerald-600 font-medium">{MOCK_DECK.status}</span>
          </div>
        </Card>

        {/* Data Room snapshot */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-violet-700 to-fuchsia-600 px-5 py-3 flex items-center justify-between">
            <div>
              <p className="text-[9px] uppercase tracking-widest text-white/70 font-semibold">Data Room</p>
              <p className="text-sm font-semibold text-white mt-0.5">{MOCK_DATA_ROOM.total_files} files · {MOCK_DATA_ROOM.total_size}</p>
            </div>
            <Lock className="w-4 h-4 text-white" />
          </div>

          {/* Category chips */}
          <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-slate-100">
            {MOCK_DATA_ROOM.categories.map((c, i) => (
              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full bg-${c.color}-100 text-${c.color}-700 font-medium`}
                    data-testid={`demo-dr-cat-${i}`}>
                {c.key} · {c.count}
              </span>
            ))}
          </div>

          {/* Sample files */}
          <div className="px-5 py-2.5 space-y-1.5">
            {MOCK_DATA_ROOM.sample_files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`demo-dr-file-${i}`}>
                <FileText className="w-3 h-3 text-slate-400 shrink-0" />
                <span className="text-slate-800 font-medium truncate flex-1">{f.name}</span>
                <span className="text-slate-400">{f.cat}</span>
                <span className="text-slate-400 w-12 text-right">{f.size}</span>
              </div>
            ))}
          </div>
          <div className="px-5 py-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center justify-between">
            <span>Tokenised links · Password-protected · Revocable</span>
            <span className="text-violet-600 font-medium">3 active shares</span>
          </div>
        </Card>
      </div>

      {/* Row 2: CDA Tracker + Exclusion List (side-by-side) */}
      <div className="grid grid-cols-2 gap-4" data-testid="demo-cda-exclusion">
        {/* CDA Tracker */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-xs font-semibold text-slate-900">CDA Tracker</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{MOCK_CDA.length} in flight</Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {MOCK_CDA.map((c, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center gap-3" data-testid={`demo-cda-${i}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TONE_DOT[c.tone]}`} />
                <p className="text-[12px] font-medium text-slate-900 w-32 truncate">{c.pharma}</p>
                <p className="text-[11px] text-slate-600 flex-1">{c.status}</p>
                <p className="text-[10px] text-slate-400 shrink-0">{c.signed}</p>
              </div>
            ))}
          </div>
          <div className="px-5 py-2 border-t border-slate-100 text-[10px] text-slate-500">
            Data Room shares unlock automatically the moment a CDA reaches &ldquo;Finalised&rdquo;.
          </div>
        </Card>

        {/* Exclusion List */}
        <Card className="p-5 border-rose-200 bg-rose-50/40">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-md bg-rose-600 flex items-center justify-center">
              <X className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="text-xs font-semibold text-rose-900 uppercase tracking-wider">Exclusion List</h2>
            <Badge className="bg-rose-100 text-rose-700 text-[10px]">{MOCK_EXCLUSION_LIST.length}</Badge>
          </div>
          <p className="text-[11px] text-slate-600 mb-3 leading-relaxed">
            Pharma partners you&apos;ve asked us NOT to approach — competitive overlaps, prior conversations, board-level
            preferences. The mapping engine respects this strictly.
          </p>
          <div className="space-y-2">
            {MOCK_EXCLUSION_LIST.map((c, i) => (
              <div key={i} className="bg-white rounded-md border border-rose-100 px-3 py-2" data-testid={`demo-exclusion-${i}`}>
                <p className="text-xs font-semibold text-slate-900">{c.company}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{c.reason}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Footer note */}
      <Card className="p-4 bg-slate-50 border-dashed">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          <span className="font-semibold text-slate-700">What you&apos;d see in the live CRM:</span> This same view, plus tabs for
          contacts, meetings, CDAs, Data Room files, full outreach timeline, financials, and a live AI chat copilot scoped to
          this biotech. The next tab shows the AI mapping engine running on the lead asset — within the exclusion guardrails above.
        </p>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — AI Mapping (Claude-generated target list for NVC-101)
// ─────────────────────────────────────────────────────────────────────────────
function MappingTab() {
  const lead = MOCK_ASSETS.find(a => a.is_lead);
  return (
    <div className="space-y-5" data-testid="demo-mapping-tab">
      {/* Asset context bar */}
      <Card className="p-5 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-violet-600">AI Target Mapping · Powered by Claude Sonnet 4.5</p>
            <h2 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {lead.name} → Ranked Pharma Buyers
            </h2>
            <p className="text-xs text-slate-600 mt-1">
              {lead.target} · {lead.indication} · {lead.phase}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Targets scored</p>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{MOCK_TARGETS.length}</p>
          </div>
        </div>
      </Card>

      {/* Methodology card */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-3.5 h-3.5 text-slate-600" />
          <p className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">How the engine scored these buyers</p>
        </div>
        <div className="grid grid-cols-5 gap-3 text-[11px]">
          {[
            { k: 'TA Fit', d: 'Therapeutic area & MoA overlap with buyer pipeline gaps' },
            { k: 'Stage Fit', d: 'Buyer appetite for this clinical stage (Ph2 vs preclinical)' },
            { k: 'Geo Fit', d: 'Ex-China commercialization capability + China track record' },
            { k: 'BD Signal', d: 'Recent licensing activity + stated strategic priorities' },
            { k: 'Competitive Dynamics', d: 'Defensive interest from on-market analogs' },
          ].map((c, i) => (
            <div key={i} className="bg-slate-50 rounded-lg px-3 py-2.5">
              <p className="font-semibold text-slate-900 mb-1">{c.k}</p>
              <p className="text-slate-500 leading-snug">{c.d}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Exclusion-list reminder banner */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/50 px-4 py-2.5 flex items-center gap-3" data-testid="demo-mapping-exclusion-banner">
        <X className="w-4 h-4 text-rose-500 shrink-0" />
        <p className="text-[11px] text-slate-700 leading-snug">
          <span className="font-semibold text-rose-700">Excluded by client request:</span>{' '}
          {MOCK_EXCLUSION_LIST.map(e => e.company).join(' · ')}
          <span className="text-slate-500"> — these companies are hard-filtered out of the ranking below.</span>
        </p>
      </div>

      {/* Ranked target list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Ranked Targets</p>
          <p className="text-[10px] text-slate-400">Score out of 45 · Tiered by overall fit</p>
        </div>
        {MOCK_TARGETS.map((t, i) => (
          <div key={i} className="px-5 py-4 border-b border-slate-100 last:border-b-0" data-testid={`demo-target-${i}`}>
            <div className="flex items-start gap-3">
              <div className="text-2xl font-bold text-slate-300 w-7 shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <p className="text-sm font-bold text-slate-900">{t.company}</p>
                  <span className="text-[10px] text-slate-400">{t.country}</span>
                  <Badge className={`text-[10px] ${TIER_COLORS[t.tier]}`}>{t.tier}</Badge>
                  <Badge className={`text-[10px] ${FIT_COLORS[t.fit]}`}>{t.fit}</Badge>
                  <span className="text-[10px] text-slate-500 font-mono font-semibold" title="AI-computed strategic fit for this asset (0–100)">{Math.round(t.score)}/100</span>
                  <span className={`text-[9px] font-semibold ${
                    t.confidence === 'High' ? 'text-emerald-600' : t.confidence === 'Medium' ? 'text-amber-600' : 'text-slate-400'
                  }`}>{t.confidence} confidence</span>
                </div>
                <p className="text-[10px] text-slate-400 mb-2">{t.archetype} · <span className="text-[#00709B]">{t.deal_type}</span></p>
                <p className="text-xs text-slate-700 leading-relaxed">{t.rationale}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Card className="p-4 bg-slate-50 border-dashed">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          <span className="font-semibold text-slate-700">How we handle approval:</span> We don&apos;t ask you to approve or
          reject each pharma post-hoc — that would inadvertently reveal our scoring methodology. Instead, we collect your{' '}
          <span className="text-emerald-700 font-medium">interest list</span> and{' '}
          <span className="text-rose-700 font-medium">exclusion list</span> upfront (shown on the Profile tab), and the
          engine ranks strictly within those guardrails. You see who we&apos;re targeting and the rationale for each — but
          the underlying ranking math stays opaque.
        </p>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Shareable Link Preview (public status page mockup)
// ─────────────────────────────────────────────────────────────────────────────
function ShareableTab() {
  // Counts that match the real AssetCard's 4-stat row exactly:
  //   Pharmas Contacted · Actively Evaluating · Awaiting Trial Data · Declined
  const pharmasContacted = MOCK_RESPONSES.length;
  const activelyEvaluating = MOCK_RESPONSES.filter(r =>
    ['Asset Under Evaluation', 'Due Diligence', 'CDA Signed', 'Meeting Scheduled', 'In Negotiation'].includes(r.status)
  ).length;
  const awaitingTrialData = MOCK_RESPONSES.filter(r => r.status === 'Awaiting Trial Data').length;
  const declined = MOCK_RESPONSES.filter(r => r.status === 'Declined').length;

  return (
    <div className="space-y-5" data-testid="demo-shareable-tab">
      {/* Link banner — kept above the mock to remind the user this is a preview */}
      <Card className="p-4 border border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
            <Share2 className="w-4 h-4 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Private Shareable Link</p>
            <p className="text-sm font-mono text-slate-900 truncate mt-0.5">
              foresightrx.com/status/<span className="text-emerald-600">3f9d-1ce7-4ab2</span>
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Tokenized · Revocable · Selection &amp; copy disabled · Contact names anonymized
            </p>
          </div>
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">DEMO</Badge>
        </div>
      </Card>

      {/* Mock browser preview — pixel-mirror of the real PublicStatusPage */}
      <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm bg-white" data-testid="demo-public-preview">
        {/* Browser chrome */}
        <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] text-slate-500 font-mono">foresightrx.com/status/3f9d-1ce7-4ab2</span>
        </div>

        <div className="bg-slate-50/30 select-none">
          {/* Top bar — mirrors PublicStatusPage */}
          <div className="border-b border-slate-100 bg-white">
            <div className="px-5 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold tracking-widest uppercase text-slate-400" style={{ letterSpacing: '0.15em' }}>
                ForesightRx
              </span>
              <span className="text-[10px] text-slate-400">Deal Advisory Status</span>
            </div>
          </div>

          <div className="px-5 py-6 space-y-5">
            {/* Hero — dark gradient w/ same body copy as production */}
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-6">
                <p className="text-white/90 text-sm leading-relaxed">
                  <span className="font-semibold text-white">ForesightRx</span> is the deal advisory arm of Marksman Healthcare, specializing in cross-border business development for life sciences. We connect biotech innovators with the right pharma partners and investors — managing strategic matchmaking, targeted outreach, and fundraising support to help early and mid-stage companies scale globally.
                </p>
              </div>
              <div className="px-6 py-5 border-b border-slate-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {MOCK_COMPANY.name}
                    </h1>
                    <p className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{MOCK_COMPANY.ta_focus}</p>
                  </div>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium border border-slate-200 bg-white text-slate-700"
                  >
                    <FileText className="w-3 h-3" />
                    <span>Non-Confidential Deck</span>
                  </a>
                </div>
              </div>
              <div className="px-6 py-4 grid grid-cols-2 gap-4">
                <RealStat label="Portfolio Assets" value={MOCK_ASSETS.length} icon={FlaskConical} tone="fuchsia" />
                <RealStat label="Under Active Outreach" value={1} icon={Activity} tone="emerald" />
              </div>
            </div>

            {/* Active outreach section header */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Active Outreach</h2>
                <span className="text-[10px] text-slate-400">(1 of {MOCK_ASSETS.length})</span>
              </div>

              {/* Active asset card — mirrors real AssetCard styling exactly:
                  border-l-4 border-l-emerald-500, 4-stat row, response grid */}
              <div className="bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-md overflow-hidden">
                {/* Card header */}
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center text-[11px] font-bold text-emerald-700">1</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>NVC-101</span>
                        <Badge className="text-[10px] border bg-violet-50 text-violet-700 border-violet-200">Phase 2</Badge>
                        <span className="text-[11px] text-slate-500">· Oncology</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        CNS-penetrant ROS1 / NTRK inhibitor — NSCLC (ROS1+), solid tumors
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4-stat row — identical to real PublicStatusPage */}
                <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-4 gap-4" data-testid="demo-asset-stats">
                  <RealAssetStat label="Pharmas Contacted" value={pharmasContacted} tone="violet" />
                  <RealAssetStat label="Actively Evaluating" value={activelyEvaluating} tone="amber" highlight={activelyEvaluating > 0} />
                  <RealAssetStat label="Awaiting Trial Data" value={awaitingTrialData} tone="orange" highlight={awaitingTrialData > 0} />
                  <RealAssetStat label="Declined" value={declined} tone="rose" />
                </div>

                {/* Asset brief paragraph (mirrors real synthesis) */}
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/40">
                  <p className="text-xs text-slate-700 leading-relaxed">
                    NVC-101 has reached {pharmasContacted} pharma partners across US, EU, and Japan.
                    {activelyEvaluating} are actively evaluating the non-confidential deck or in formal due diligence,
                    {awaitingTrialData} are waiting for the Phase 2 readout before committing diligence, and
                    {declined} have declined with explicit reasons (oncology restructuring, TA pivot, overlapping internal program).
                    Current focus: convert Pfizer + AstraZeneca to term-sheet-stage post-P2 topline.
                  </p>
                </div>

                {/* Pharma Responses — exact mirror of real ResponseRow grid */}
                <div className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Pharma Responses</span>
                    <span className="text-[10px] text-slate-400">({MOCK_RESPONSES.length})</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {MOCK_RESPONSES.map((r, i) => (
                      <RealResponseRow key={i} r={r} testid={`demo-response-${i}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* What happens next — mirrors real footer block */}
            <div className="rounded-lg border border-slate-200 bg-blue-50/30 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">What Happens Next</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Once a pharma partner completes their evaluation and signals genuine interest, ForesightRx will coordinate the next step — whether that&apos;s a virtual call, in-person meeting, or further data exchange — and loop you in directly.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 text-center">
              <p className="text-[11px] text-slate-400">
                Powered by <span className="font-semibold text-slate-500">ForesightRx</span> — A division of Marksman Healthcare
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="p-4 bg-slate-50 border-dashed">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          <span className="font-semibold text-slate-700">Privacy &amp; control:</span> Each share link is bound to a single
          recipient, content is non-selectable, contact designations are coarse-grained (e.g. &ldquo;VP, BD &amp; Licensing&rdquo; not
          full titles), and you can revoke access in one click. The biotech sees outreach status — never the underlying pharma
          contact details. Status terms (<span className="text-amber-700">Asset Under Evaluation</span> ·{' '}
          <span className="text-orange-700">Awaiting Trial Data</span> ·{' '}
          <span className="text-indigo-700">Due Diligence</span> ·{' '}
          <span className="text-rose-700">Declined</span>) mirror the live CRM exactly.
        </p>
      </Card>
    </div>
  );
}

// ── Mirror of PublicStatusPage's <Stat> component ────────────────────────
function RealStat({ label, value, icon: Icon, tone = 'slate' }) {
  const toneClass = {
    fuchsia: 'text-fuchsia-600 bg-fuchsia-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    slate: 'text-slate-500 bg-slate-100',
  }[tone] || 'text-slate-500 bg-slate-100';
  return (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-slate-900 leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-medium">{label}</p>
      </div>
    </div>
  );
}

// ── Mirror of PublicStatusPage's <AssetStat> component ───────────────────
function RealAssetStat({ label, value, tone = 'slate', highlight = false }) {
  const valueColor = {
    violet: highlight ? 'text-violet-600' : 'text-violet-700',
    amber: highlight ? 'text-amber-600' : 'text-amber-700',
    orange: highlight ? 'text-orange-600' : 'text-orange-700',
    rose: 'text-rose-600',
    slate: 'text-slate-700',
  }[tone] || 'text-slate-700';
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold leading-none ${valueColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1.5 font-medium">{label}</p>
    </div>
  );
}

// ── Mirror of PublicStatusPage's <ResponseRow> component ─────────────────
function RealResponseRow({ r, testid }) {
  const colorCls = REAL_STATUS_COLORS[r.status] || 'bg-slate-50 text-slate-600 border-slate-200';
  const isDeclined = r.status === 'Declined';
  return (
    <div className={`rounded-md border px-3 py-2.5 ${isDeclined ? 'bg-rose-50/50 border-rose-200' : 'bg-slate-50/40 border-slate-100'}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-900 truncate">{r.company}</p>
          <p className="text-[9px] text-slate-400">{r.country}</p>
        </div>
        <Badge className={`text-[9px] border ${colorCls} shrink-0`}>{r.status}</Badge>
      </div>
      <p className={`text-[10.5px] leading-snug mb-1.5 ${isDeclined ? 'text-rose-700 font-medium' : 'text-slate-700'}`}>
        {r.sub_reason}
      </p>
      <div className="text-[9px] text-slate-400 space-y-0.5">
        <p><span className="font-medium text-slate-500">Last:</span> {r.last_touch}</p>
        <p><span className="font-medium text-slate-500">Next:</span> <span className={isDeclined ? 'text-rose-600' : 'text-slate-700'}>{r.next_event}</span></p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main demo page
// ─────────────────────────────────────────────────────────────────────────────
export default function DemoPage() {
  const [tab, setTab] = useState('profile');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="demo-page">
      <DemoBanner />

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <p className="text-[10px] uppercase tracking-widest font-semibold text-violet-600">Client Walkthrough</p>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How ForesightRx works for your biotech
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            A 5-minute walkthrough using a fictional Phase-2 oncology biotech. We&apos;ll show you how your company looks in our
            CRM, how our AI engine ranks pharma buyers for your lead asset, and the private status link you&apos;d receive to
            track every interaction in real-time.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-5 inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="demo-tabs">
          <TabButton
            active={tab === 'profile'} onClick={() => setTab('profile')}
            icon={Building2} label="Biotech Profile" num="1" testid="demo-tab-profile"
          />
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <TabButton
            active={tab === 'mapping'} onClick={() => setTab('mapping')}
            icon={Sparkles} label="AI Mapping" num="2" testid="demo-tab-mapping"
          />
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <TabButton
            active={tab === 'shareable'} onClick={() => setTab('shareable')}
            icon={Share2} label="Shareable Link" num="3" testid="demo-tab-shareable"
          />
        </div>

        {/* Active tab */}
        {tab === 'profile' && <ProfileTab />}
        {tab === 'mapping' && <MappingTab />}
        {tab === 'shareable' && <ShareableTab />}

        {/* Bottom nav between tabs */}
        <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-5">
          <div>
            {tab !== 'profile' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTab(tab === 'shareable' ? 'mapping' : 'profile')}
                data-testid="demo-prev-btn"
              >
                ← Back
              </Button>
            )}
          </div>
          <p className="text-[10px] text-slate-400">
            {tab === 'profile' && '1 of 3 · Biotech Profile'}
            {tab === 'mapping' && '2 of 3 · AI Mapping'}
            {tab === 'shareable' && '3 of 3 · Shareable Link'}
          </p>
          <div>
            {tab !== 'shareable' && (
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                onClick={() => setTab(tab === 'profile' ? 'mapping' : 'shareable')}
                data-testid="demo-next-btn"
              >
                Next: {tab === 'profile' ? 'AI Mapping' : 'Shareable Link'} →
              </Button>
            )}
            {tab === 'shareable' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTab('profile')}
                data-testid="demo-restart-btn"
              >
                ↺ Restart walkthrough
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
